import type { LoaderFunctionArgs } from "react-router";
import { appStoreAuthorization } from "src/configs/authorization/app-store";
import { communicationsAuthorization } from "src/configs/authorization/communication";
import { customAnalyticsAuthorization } from "src/configs/authorization/custom-analytics";
import { experienceDesignAuthorization } from "src/configs/authorization/experience-design";
import { promiseAuthorization } from "src/configs/authorization/promise";
import { promotionsAuthorization } from "src/configs/authorization/promotions";
import { trackingsAuthorization } from "src/configs/authorization/trackings";
import { PAGES, PAGE_URLS } from "src/configs/pages-urls";
import appStoreRoutes from "src/module--app-store/app-store.router";
import campaignRoutes from "src/module--campaigns/campaigns.router";
import communicationsRoutes from "src/module--communications/communications.router";
import customAnalyticsRoutes from "src/module--custom-analytics/customAnalytics.router";
import journeyRoutes from "src/module--journey/journey.router";
import promisesRoutes from "src/module--promise/promise.router";
import returnPromotionRoutes from "src/module--returns/returns.promotion.router";
import returnsRoutes from "src/module--returns/returns.router";
import settingsRoutes from "src/module--settings/settings.router";
import surveyRoutes from "src/module--survey/survey.router";
import trackingsRoutes from "src/module--trackings/trackings.router";
import { authorizationLoader } from "src/router/common-loaders";
import serviceRoutes from "src/router/service.router";
import { convertFrameworkRoute } from "src/utils/routes";

export default [
	{
		index: true,
		lazy: () =>
			import("src/pages/landing-page").then(convertFrameworkRoute),
	},
	{
		path: PAGES.settings.path,
		children: settingsRoutes,
	},
	{
		path: PAGES.trackings.path,
		children: trackingsRoutes,
		loader: ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				trackingsAuthorization.trackings.index,
			),
	},
	{
		path: PAGES.communications.path,
		children: communicationsRoutes,
		loader: ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				communicationsAuthorization.communications.index,
			),
	},
	{
		path: PAGES.experienceDesign.path,
		children: [
			{
				index: true,
				lazy: () =>
					import(
						"src/module--journey/pages/ExperienceDesignLanding.page"
					).then(convertFrameworkRoute),
				loader: async ({
					params: { accountId: accountIdParam },
				}: LoaderFunctionArgs) =>
					authorizationLoader(
						{
							accountIdParam,
							conditionalRedirect: true,
						},
						experienceDesignAuthorization.experienceDesign.index,
					),
			},
			...journeyRoutes,
			...campaignRoutes,
			...surveyRoutes,
		],
		loader: async ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				experienceDesignAuthorization.experienceDesign.index,
			),
	},
	{
		path: PAGES.returns.path,
		lazy: () =>
			import("src/module--returns/returns.base-page").then(
				convertFrameworkRoute,
			),
		children: returnsRoutes,
	},
	{
		path: `${PAGES.returns.path}/${PAGES.returns.sub.returnsLanding}`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsLanding.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returnsPromotion.path,
		children: returnPromotionRoutes,
		loader: ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				promotionsAuthorization.returnsPromotion.index,
			),
	},
	{
		path: PAGES.promise.path,
		children: promisesRoutes,
		loader: ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				promiseAuthorization.promises.index,
			),
	},
	{
		path: PAGES.appStore.path,
		children: appStoreRoutes,
		loader: ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				appStoreAuthorization.appStoreAndBuilder.index,
			),
	},
	{
		path: `${PAGES.customAnalytics.path}/${PAGES.customAnalytics.sub.premiumAnalyticsLanding}`,
		lazy: () =>
			import(
				"src/module--custom-analytics/pages/PremiumAnalyticsLanding.page"
			).then(convertFrameworkRoute),
		loader: async ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				customAnalyticsAuthorization.customAnalytics
					.premiumAnalyticsLanding,
				[
					{
						redirectUrl: PAGE_URLS.customAnalytics().index,
						abilityArguments: {},
					},
				],
			),
	},
	{
		path: PAGES.customAnalytics.path,
		loader: async ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				customAnalyticsAuthorization.customAnalytics.index,
				[
					{
						redirectUrl:
							PAGE_URLS.customAnalytics().premiumAnalyticsLanding,
						abilityArguments: {},
					},
				],
			),
		children: customAnalyticsRoutes,
	},
	{
		path: PAGES.service.path,
		children: serviceRoutes,
		loader: ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) => authorizationLoader({ accountIdParam }),
	},
	{
		path: PAGES.notAuthorized.path,
		lazy: () => import("src/pages/403").then(convertFrameworkRoute),
	},
	{
		path: "*",
		lazy: () => import("src/pages/404").then(convertFrameworkRoute),
	},
];
